//#region IMPORT
import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import M_EduVideos_BASICF from '../EduVideos/M_EduVideos_BASICF';
import M_EduDocs_BASICF from '../EduDocs/M_EduDocs_BASICF';
import M_WorkPlans_BASICF from '../WorkPlans/M_WorkPlans_BASICF';
import M_WorkPermits_BASICF from '../WorkPermits/M_WorkPermits_BASICF';
import M_ExtraMeetings_BASICF from '../ExtraMeetings/M_ExtraMeetings_BASICF';
import M_TbmEntry_BASICF from '../TbmEntry/M_TbmEntry_BASICF';
import { useSelector, useDispatch } from 'react-redux';
import QRDialog from '../ui/QRDialog/QRDialog';
import dayjs from 'dayjs';
import { TbmActions } from '../../../../Reducer/TbmReducer';
import { useTbmSessionSave } from '../hook/useSaveTbmSession';
import M_FinalSign_BASICF from '../FinalSign/M_FinalSign_BASICF';
import { Button, Dialog } from '../../../../Components/MUIs';
import Lottie from 'lottie-react';
import TbmStepTransitionOverlay from '../ui/TbmStepTransitionOverlay';
import AiAnimation from '../../../../Assets/animations/Loading.json';
import BackClickBottomSheetTbm from '../../../../Components/Ui/BackClickBottomSheetTbm';
import BottomSheet from '../../../../Components/Ui/BottomSheet';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
//#endregion

/** 진행자 전용 단계 — 근로자(참가자) stepper·화면에서 제외 */
const HOST_ONLY_STEP_CODES = new Set(['TBM_MEET', 'TBM_ENTRY']);

/**
 * 공유 activeStep(진행자 기준 전체 단계 인덱스) → 근로자용 visible 목록 인덱스
 * 진행자가 MEET/ENTRY에 있을 때는 직전 공개 단계에 머무름
 */
function ResolveWorkerDisplayStep(
  activeStep,
  orderedStepCodes,
  visibleStepCodes
) {
  if (!orderedStepCodes.length || !visibleStepCodes.length) return 0;
  const idx = Math.min(Math.max(0, activeStep), orderedStepCodes.length - 1);
  const code = orderedStepCodes[idx];
  if (!HOST_ONLY_STEP_CODES.has(code)) {
    const w = visibleStepCodes.indexOf(code);
    return w >= 0 ? w : 0;
  }
  // TBM_ENTRY(회의록 작성) 중에는 화면은 그대로 두고 서명 바텀시트만 띄움 (M_Master_BASIC 참고)
  for (let i = idx - 1; i >= 0; i -= 1) {
    if (!HOST_ONLY_STEP_CODES.has(orderedStepCodes[i])) {
      const w = visibleStepCodes.indexOf(orderedStepCodes[i]);
      return w >= 0 ? w : 0;
    }
  }
  return 0;
}

function CustomLineStepper({ activeStep, steps }) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxSizing: 'border-box',
        px: 2,
        py: 2,
      }}
    >
      {Array.from({ length: steps }).map((_, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: 4,
            backgroundColor: index <= activeStep ? '#33ABD1' : '#e0e0e0',
            borderRadius: 2,
            ml: index === 0 ? 0 : 1,
          }}
        />
      ))}
    </Box>
  );
}

function M_Master_BASIC(props) {
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => state.common.activeStep);
  const { sessionData, stepData, participantData, stepTransition } =
    useSelector((state) => state.tbm);
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const isHost = participantData.hostYn === 'Y';
  // 참석자 서명 완료 여부 (서명 후 TBM 완료 화면으로 이동)
  const [workerSigned, setWorkerSigned] = useState(false);

  // 1. stepCode → 화면 컴포넌트 (ActiveButton 변경 시만 갱신)
  const componentMap = useMemo(
    () => ({
      TBM_VIDEO: (key) => (
        <M_EduVideos_BASICF
          key={key}
          stepCode="TBM_VIDEO"
          ActiveButton={props.ActiveButton}
        />
      ),
      TBM_DOCS: (key) => (
        <M_EduDocs_BASICF
          key={key}
          stepCode="TBM_DOCS"
          ActiveButton={props.ActiveButton}
        />
      ),
      TBM_PLAN: (key) => (
        <M_WorkPlans_BASICF
          key={key}
          stepCode="TBM_PLAN"
          ActiveButton={props.ActiveButton}
        />
      ),
      TBM_PERMIT: (key) => (
        <M_WorkPermits_BASICF
          key={key}
          stepCode="TBM_PERMIT"
          ActiveButton={props.ActiveButton}
        />
      ),
      TBM_MEET: (key) => (
        <M_ExtraMeetings_BASICF
          key={key}
          stepCode="TBM_MEET"
          ActiveButton={props.ActiveButton}
        />
      ),
      TBM_ENTRY: (key) => (
        <M_TbmEntry_BASICF
          key={key}
          stepCode="TBM_ENTRY"
          ActiveButton={props.ActiveButton}
        />
      ),
      TBM_SIGN: (key) => (
        <M_FinalSign_BASICF
          key={key}
          stepCode="TBM_SIGN"
          ActiveButton={props.ActiveButton}
          TbmEnd={props.TbmEnd}
          signed={workerSigned}
          onSigned={() => setWorkerSigned(true)}
        />
      ),
    }),
    [props.ActiveButton, workerSigned]
  );

  /** 필수 단계만 stepOrder 순 — 진행자·동기화 기준 전체 순서 */
  const orderedStepCodes = useMemo(() => {
    if (!stepData?.length) return [];
    return [...stepData]
      .filter((item) => item.isRequired === 'Y')
      .sort((a, b) => a.stepOrder - b.stepOrder)
      .map((item) => item.stepCode);
  }, [stepData]);

  /** 근로자: MEET/ENTRY 제외한 단계만 화면에 올림 */
  const visibleStepCodes = useMemo(() => {
    if (isHost) return orderedStepCodes;
    return orderedStepCodes.filter((code) => !HOST_ONLY_STEP_CODES.has(code));
  }, [isHost, orderedStepCodes]);

  // 2. stepData를 기반으로 동적 컴포넌트 배열 생성
  const dynamicComponents = useMemo(() => {
    if (!visibleStepCodes.length) return [];

    return visibleStepCodes
      .map((stepCode) => {
        const componentFn = componentMap[stepCode];
        return componentFn ? componentFn(stepCode) : null;
      })
      .filter((c) => c !== null);
  }, [visibleStepCodes, componentMap]);

  /** 근로자: 공유 activeStep을 보이는 단계 인덱스로 변환 */
  const displayActiveStep = useMemo(() => {
    if (isHost) return activeStep;
    // 참석자 서명 완료 → TBM 완료(최종서명) 화면
    if (workerSigned) {
      const w = visibleStepCodes.indexOf('TBM_SIGN');
      if (w >= 0) return w;
    }
    return ResolveWorkerDisplayStep(
      activeStep,
      orderedStepCodes,
      visibleStepCodes
    );
  }, [isHost, activeStep, orderedStepCodes, visibleStepCodes, workerSigned]);

  /** 참석자: 진행자 회의록 작성(TBM_ENTRY) 중이면 현재 화면 위에 서명 바텀시트만 표시 */
  const showWorkerSignSheet =
    !isHost && !workerSigned && orderedStepCodes[activeStep] === 'TBM_ENTRY';

  // 첫 번째 단계 정보 (QR 시작 시 사용)
  const firstStepInfo =
    dynamicComponents.length > 0
      ? stepData.find((s) => s.stepCode === dynamicComponents[0].key)
      : null;

  //#region 커스텀훅
  const { SaveTbmSession } = useTbmSessionSave({
    onSuccess: (rows) => {
      dispatch(TbmActions.setQrModal(false));
    },
  });
  //#endregion

  return (
    <Box sx={{ backgroundColor: '#F3F7F8', height: '100%' }}>
      <Stack sx={{ width: '100%', height: '100%' }}>
        <CustomLineStepper
          activeStep={displayActiveStep}
          steps={dynamicComponents.length}
        />
        {/* 현재 단계의 컴포넌트 렌더링 (근로자는 진행자 전용 단계 제외 + 인덱스 매핑) */}
        {dynamicComponents[displayActiveStep] || null}
      </Stack>
      {showWorkerSignSheet && (
        <M_FinalSign_BASICF
          sheetOnly
          stepCode="TBM_SIGN"
          ActiveButton={props.ActiveButton}
          TbmEnd={props.TbmEnd}
          signed={workerSigned}
          onSigned={() => setWorkerSigned(true)}
        />
      )}
      <QRDialog
        ActiveButton={props.ActiveButton}
        onStart={({ sessionCd }) => {
          if (!firstStepInfo) return;

          // 교육 시작 : 첫번째 단계 저장, 시작일, 세션상태 업데이트
          SaveTbmSession([
            {
              ...sessionData,
              startDt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
              curStepOrder: firstStepInfo.stepOrder,
              curStepCode: firstStepInfo.stepCode,
              curPosition: firstStepInfo.lastPosition,
              sessionStatus: 'OPEN',
            },
          ]);
        }}
      />
      {/* 바텀시트 부분 */}
      {bottomSheetState.type === 'backClickTbm' && (
        <BackClickBottomSheetTbm
          DeleteLogic={props.Delete1}
          ActiveButton={props.ActiveButton}
        />
      )}
      {/* 추가회의 바텀 드로워 */}
      {bottomSheetState.type === 'meetingDrawer' && (
        <BottomSheet
          isHeaderChildren={true}
          headerText={''}
          headerChildren={
            <Typography
              sx={{ fontSize: '18px', fontWeight: 'bold', display: 'flex' }}
            >
              추가 회의
            </Typography>
          }
        >
          <Box sx={{ p: '8px' }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              {/* 안내 텍스트 */}
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold', my: 4 }}>
                추가 회의를 진행하시겠습니까?
              </Typography>

              {/* 버튼 */}
              <Stack
                sx={{
                  m: 2,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignContent: 'center',
                }}
                direction={'row'}
                spacing={3}
              >
                <Button
                  variant="outlined"
                  sx={{
                    width: '100%',
                    height: '36px',
                    borderRadius: '18px',
                    px: 0,
                    border: 2,
                    '&:hover': {
                      border: 2,
                    },
                  }}
                  name={
                    <>
                      <Typography sx={{ fontSize: '14px', color: '#575757' }}>
                        안전보건 회의 시작
                      </Typography>
                    </>
                  }
                  onClick={() => {
                    dispatch(BottomSheetActions.CloseBottomSheet());
                    dispatch(TbmActions.setMeetFlag(true));
                    dispatch(CommonActions.setActiveStep(activeStep + 1));
                    // 추가회의 진행 중 참가자는 대기 모달 (회의 종료 시 최종서명으로 전환)
                    props.ActiveButton({
                      type: 'end',
                      content: true,
                      waiting: 'Y',
                    });
                  }}
                />
                <Button
                  sx={{
                    width: '100%',
                    height: '36px',
                    borderRadius: '18px',
                  }}
                  name="회의 건너뛰기"
                  onClick={() => {
                    dispatch(BottomSheetActions.CloseBottomSheet());
                    dispatch(TbmActions.setMeetFlag(false));
                    dispatch(CommonActions.setActiveStep(activeStep + 2));
                    // 회의록 작성(TBM_ENTRY)으로 바로 이동: 참가자는 대기 없이 최종서명으로 전환
                    props.ActiveButton({ type: 'stepChange', step: activeStep + 2 });
                    props.ActiveButton({
                      type: 'end',
                      content: true,
                      waiting: 'N',
                    });
                  }}
                />
              </Stack>
            </Box>
          </Box>
        </BottomSheet>
      )}
      {props.modalToggle3 && (
        <Dialog
          open={props.modalToggle3}
          // onClose={(e) => props.setModalToggle1(false)}
          sx={{ height: '90vh' }}
          titleSx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#D9D9D9',
            pt: 3,
            pb: 1.5,
          }}
          contentSx={{ display: 'flex', flexDirection: 'column', p: 3, px: 3 }}
          actionsSx={{ p: 0 }}
          PaperProps={{
            sx: {
              m: 2,
              borderRadius: 4,
              width: 'calc(100% - 32px)', // 좌우 마진 고려한 전체 너비
              maxWidth: '100%', // 혹시 모를 제한 제거
            },
          }}
          fullWidth={true}
          saveButtonName="선택"
          closeButtonName="닫기"
          useSaveButton={false}
          useSaveAfterClose={false}
          useCloseButton={false}
          onSave={() => {}}
          maxWidth={false}
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Lottie
                animationData={AiAnimation}
                loop
                autoplay
                style={{
                  display: 'flex',
                  width: '50px',
                  height: '30px',
                  overflow: 'hidden',
                  transform: 'scale(4)',
                }}
              />
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                  교육이 종료됩니다.
                </Typography>
                <Typography>관리자가 교육을 종료했어요.</Typography>
                <Typography>잠시 후 홈으로 이동합니다.</Typography>
              </Box>
            </Box>
          }
        />
      )}
      {props.modalToggle4 && (
        <Dialog
          open={props.modalToggle4}
          // onClose={(e) => props.setModalToggle4(false)}
          sx={{ height: '90vh' }}
          titleSx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#D9D9D9',
            pt: 3,
            pb: 1.5,
          }}
          contentSx={{ display: 'flex', flexDirection: 'column', p: 3, px: 3 }}
          actionsSx={{ p: 0 }}
          PaperProps={{
            sx: {
              m: 2,
              borderRadius: 4,
              width: 'calc(100% - 32px)', // 좌우 마진 고려한 전체 너비
              maxWidth: '100%', // 혹시 모를 제한 제거
            },
          }}
          fullWidth={true}
          saveButtonName="선택"
          closeButtonName="닫기"
          useSaveButton={false}
          useSaveAfterClose={false}
          useCloseButton={false}
          onSave={() => {}}
          maxWidth={false}
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Lottie
                animationData={AiAnimation}
                loop
                autoplay
                style={{
                  display: 'flex',
                  width: '50px',
                  height: '30px',
                  overflow: 'hidden',
                  transform: 'scale(4)',
                }}
              />
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                  회의록 작성 중입니다.
                </Typography>
                <Typography>
                  {dynamicComponents.length === 1
                    ? '작업 전 안전점검 회의(TBM)을 진행합니다.'
                    : '관리자가 회의록을 작성하고 있어요.'}
                </Typography>
                <Typography>
                  {dynamicComponents.length === 1
                    ? '관리감독자의 진행에 따라주세요.'
                    : '잠시만 기다려 주세요.'}
                </Typography>
              </Box>
            </Box>
          }
        />
      )}
      {/* 단계 이동 중 모달 */}
      <TbmStepTransitionOverlay
        open={stepTransition.isTransitioning}
        direction={stepTransition.direction}
      />
    </Box>
  );
}

export default M_Master_BASIC;
